import '@/styles/style.css';

import ui from '@nuxt/ui/vue-plugin';
import { createPinia } from 'pinia';
import type { Plugin } from 'vue';
import { createApp } from 'vue';

import { setupValibotI18n } from '@support/core';

import App from '@/App.vue';
import { apolloClient } from '@/core/apollo/client';
import { setupLaunchQueue } from '@/core/pwa/launch-queue';
import router from '@/core/router';
import { useTheme } from '@/shared/composables/useTheme';

setupValibotI18n();

// Initialize the color-mode singleton before mount so the resolved theme class
// is applied to <html> as early as possible (the inline script in index.html
// handles the very first paint).
useTheme();

import * as plugins from '@/core/plugins';
import * as ApolloComposable from '@vue/apollo-composable';

import { registerModules } from '@/core/register-modules.ts';

import { AuthModule } from '@/modules/auth';
import { NotificationsModule } from './modules/notifications';
import { OrganizationsModule } from './modules/organizations';
import { PublishersModule } from './modules/publishers';
import { TicketsModule } from './modules/tickets';
import { UsersModule } from './modules/users';

registerModules({
  AuthModule,
  NotificationsModule,
  OrganizationsModule,
  PublishersModule,
  TicketsModule,
  UsersModule,
});

// Component playgrounds, plus the widget sandbox. Gated behind a DYNAMIC import
// rather than a plain `if` around a static one: both conditions below fold to
// constants at build time, so Vite drops this branch together with everything
// it references and the pages leave the production bundle entirely — not merely
// become unroutable in it.
//
// `VITE_APP_ENV === 'local'` is here as well as `DEV` because the e2e suite runs
// against a PREVIEW build (`build:frontend` + `preview:frontend`), where `DEV`
// is false — so gating on `DEV` alone meant `/sandbox/widget` could never be
// covered by a spec, which is most of what it was built for. `VITE_APP_ENV` is
// `local` only in a workspace's generated `.env`; a deployed build sets its own,
// so the branch still folds away in production.
//
// That matters more than for a playground: the widget sandbox mints identity
// assertions. It is inert without the API's dev-only mint mutation, which
// `PlaywrightModule` refuses to load in production — but the page should not
// ship regardless.
if (import.meta.env.DEV || import.meta.env.VITE_APP_ENV === 'local') {
  const { SandboxModule } = await import('./modules/sandbox');

  registerModules({ SandboxModule });
}

const app = createApp(App);

app.use(createPinia());
app.use(router);
app.use(ui);
app.provide(ApolloComposable.ApolloClients, {
  default: apolloClient,
});

for (const plugin of Object.values(plugins) as Plugin[]) {
  app.use(plugin);
}

router.isReady().then(() => {
  app.mount('#app');
  setupLaunchQueue(router);
});
